/**
 * API client for the Tier-1 notifications + watchlist feature.
 *
 * All routes are JWT-scoped on the backend (the calling user is read from
 * the verified Clerk `sub` — never from path or body). Each helper takes
 * a `getToken` accessor that resolves to the current session JWT at call
 * time so token rotation is transparent.
 *
 * Wire path: helper → fetchApi/mutateApi → client.ts resolveUrl rewrites
 * `/v1/...` → `/db/...` → routers/notifications.py.
 */
import { fetchApi, mutateApi, type GetToken } from '../api/client';


// ── Types (mirror routers/notifications.py response shapes) ────────────────

/** All notification kinds the backend can emit today. Frontend renders
 *  per-kind with a localized message + icon. */
export type NotificationKind =
  | 'vote_result'
  | 'bill_status'
  | 'session_reminder'
  | 'member_vote'        // Watched councillor cast a final vote
  | 'member_speech';     // Watched councillor spoke on the floor about a bill

export interface NotificationItem {
  id:         string;
  kind:       NotificationKind;
  /** Untyped JSON payload — shape varies by `kind`. Reader code should
   *  switch on `kind` before accessing fields. */
  payload:    Record<string, unknown>;
  /** Internal dashboard route to navigate to on click (e.g. `/bills/22.061`). */
  target_url: string | null;
  created_at: string | null;
  read_at:    string | null;
}

/** Lightweight bundle returned by /notifications/bundle — combines the
 *  bell dropdown's items + the unread count in a single round-trip. */
export interface NotificationBundle {
  items:  NotificationItem[];
  unread: number;
}

export type WatchKind = 'bills' | 'members' | 'votes';


// ── Feed endpoints ─────────────────────────────────────────────────────────

export function fetchNotificationBundle(
  getToken: GetToken,
  limit = 10,
): Promise<NotificationBundle> {
  return fetchApi<NotificationBundle>('/v1/notifications/bundle', { limit }, { getToken });
}

export function fetchUnreadCount(getToken: GetToken): Promise<{ unread: number }> {
  return fetchApi<{ unread: number }>('/v1/notifications/unread-count', {}, { getToken });
}

export function fetchNotificationList(
  getToken:   GetToken,
  opts: { limit?: number; only_unread?: boolean } = {},
): Promise<{ items: NotificationItem[] }> {
  return fetchApi<{ items: NotificationItem[] }>(
    '/v1/notifications',
    { limit: opts.limit ?? 50, only_unread: opts.only_unread ?? false },
    { getToken },
  );
}

/** Idempotent — re-reading an already-read row is a no-op server-side. */
export function markNotificationRead(
  getToken:       GetToken,
  notificationId: string,
): Promise<{ ok: true }> {
  return mutateApi<{ ok: true }>(
    `/v1/notifications/${encodeURIComponent(notificationId)}/read`,
    'PATCH',
    null,
    { getToken },
  );
}

export function markAllNotificationsRead(
  getToken: GetToken,
): Promise<{ ok: true; marked: number }> {
  return mutateApi<{ ok: true; marked: number }>(
    '/v1/notifications/mark-all-read',
    'POST',
    null,
    { getToken },
  );
}


// ── Watchlist endpoints ────────────────────────────────────────────────────

/** Add to watchlist. Backend caps at 100 items per kind and returns 409
 *  if full — the caller should surface a "Watchlist full" toast. */
export function addToWatchlist(
  getToken: GetToken,
  kind:     WatchKind,
  itemId:   string,
): Promise<{ ok: true; is_watching: true; size: number }> {
  return mutateApi(
    `/v1/watchlist/${kind}/${encodeURIComponent(itemId)}`,
    'POST',
    null,
    { getToken },
  );
}

export function removeFromWatchlist(
  getToken: GetToken,
  kind:     WatchKind,
  itemId:   string,
): Promise<{ ok: true; is_watching: false }> {
  return mutateApi(
    `/v1/watchlist/${kind}/${encodeURIComponent(itemId)}`,
    'DELETE',
    null,
    { getToken },
  );
}

/** Bookmark icon state — filled if true, outlined if false. */
export function checkIsWatching(
  getToken: GetToken,
  kind:     WatchKind,
  itemId:   string,
): Promise<{ is_watching: boolean }> {
  return fetchApi<{ is_watching: boolean }>(
    `/v1/watchlist/${kind}/${encodeURIComponent(itemId)}`,
    {},
    { getToken },
  );
}

/** Full list for the Settings page's expanded watchlist view (bare ids). */
export function listWatchlist(
  getToken: GetToken,
  kind:     WatchKind,
): Promise<{ items: string[] }> {
  return fetchApi<{ items: string[] }>(
    `/v1/watchlist/${kind}`,
    {},
    { getToken },
  );
}


/** One row in a hydrated watchlist response. `display_title === null`
 *  means the source entity was either never present (stale id) or
 *  removed since the watch was saved — UI should mark as "unavailable"
 *  and offer a quick-remove. */
export interface WatchlistDetailItem {
  id:             string;
  display_title:  string | null;
  subtitle:       string | null;
  status:         string | null;
  /** Tags attached to this item, already sorted alphabetical by the
   *  backend. Empty array when no tags attached. Each tag carries id,
   *  name, and optional hex colour — same shape as `Tag` in tagsApi.ts
   *  but inlined here to avoid a circular type import. */
  tags:           Array<{ id: string; name: string; color: string | null }>;
}

/** Hydrated list — used by the watchlist-management modal and the
 *  per-page "watchlist tag filter" surface on Bills + Members.
 *
 *  Optional `tagIds` filters to items that have ALL listed tags attached
 *  (AND semantics, matching the modal filter bar). Empty / undefined =
 *  return everything in the user's watchlist for that kind.
 *
 *  Bills hydrate with title + business_type + status; members with name
 *  + party + canton; votes return bare ids only. */
export function fetchWatchlistDetails(
  getToken: GetToken,
  kind:     WatchKind,
  tagIds?:  string[],
): Promise<{ items: WatchlistDetailItem[] }> {
  const params: Record<string, string> = {};
  if (tagIds && tagIds.length > 0) {
    params.tag_ids = tagIds.join(',');
  }
  return fetchApi<{ items: WatchlistDetailItem[] }>(
    `/v1/watchlist/${kind}/details`,
    params,
    { getToken },
  );
}
